import { cn } from '@/styles/tailwind-utils';

interface BrandLoaderProps {
  /** Primary line shown beneath the mark. */
  title?: string;
  /** Secondary, muted line shown beneath the title. */
  subtitle?: string;
  /** Override the wrapper layout/padding (defaults to a generous centered block). */
  className?: string;
}

// Clockwise stagger for the 2×2 brand mark: TL → TR → BR → BL.
const PULSE_DELAYS = ['0ms', '150ms', '450ms', '300ms'];

/**
 * Brand-aligned loading indicator that animates the Calendo mark (a 2×2 grid of
 * rounded squares) with a clockwise staggered pulse — a lightweight, on-brand
 * alternative to a generic spinner. Shown while data is fetched from the API.
 */
export function BrandLoader({ title, subtitle, className }: BrandLoaderProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn('flex flex-col items-center justify-center px-5 py-24 text-center', className)}
    >
      <style>{`@keyframes calendo-mark-pulse{0%,100%{opacity:.2;transform:scale(.8)}40%{opacity:1;transform:scale(1)}}`}</style>
      <div className="relative flex h-14 w-14 items-center justify-center">
        <span className="absolute inset-0 rounded-2xl bg-brand-subtle" aria-hidden="true" />
        <div className="relative grid h-7 w-7 grid-cols-2 gap-1" aria-hidden="true">
          {PULSE_DELAYS.map((delay, i) => (
            <span
              key={i}
              className="rounded-[3px] bg-primary"
              style={{ animation: 'calendo-mark-pulse 1.2s ease-in-out infinite', animationDelay: delay }}
            />
          ))}
        </div>
      </div>
      {title && <p className="mt-5 text-sm font-medium text-foreground">{title}</p>}
      {subtitle && <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>}
    </div>
  );
}
